import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Award, BarChart3, Briefcase, Calendar, Clock, Cpu, FileText, Flag, FolderOpen, HelpCircle,
  Home, Image, Inbox, LayoutDashboard, List, LogOut, Menu, MessageSquareQuote,
  PenLine, Route, Settings, Square, User, Users,
} from 'lucide-react';
import { useAdminAuth, ROLE_LABELS } from './AdminAuth';
import { ADMIN_NAV } from './resources';
import { useSeo } from '@/lib/seo';
import { cn } from '@/lib/utils';

const ICONS = {
  home: LayoutDashboard, file: FileText, briefcase: Briefcase, cpu: Cpu,
  grid: Square, award: Award, pen: PenLine, user: User, help: HelpCircle,
  quote: MessageSquareQuote, image: Image, folder: FolderOpen, calendar: Calendar,
  clock: Clock, inbox: Inbox, menu: Menu, route: Route, settings: Settings,
  users: Users, list: List, flag: Flag, chart: BarChart3,
};

// Clean internal-tool shell: navy sidebar, light content area, restrained.
export function AdminShell() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  useSeo({ title: 'Amden Admin', robots: 'noindex, nofollow' });

  const signOut = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-shell">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-navy-900 lg:flex">
        <div className="border-b border-white/10 px-5 py-5">
          <p className="text-sm font-extrabold tracking-tight text-white">Amden Group</p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gold-500">Administration</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Admin">
          <ul className="space-y-0.5">
            {ADMIN_NAV.filter((i) => i.roles.includes(user.role)).map((item) => {
              const Icon = ICONS[item.icon] || Square;
              return (
                <li key={item.label}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/admin'}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors',
                        isActive ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'
                      )
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="border-t border-white/10 p-4">
          <p className="truncate text-sm font-bold text-white">{user.name}</p>
          <p className="text-xs text-slate-400">{ROLE_LABELS[user.role]}</p>
          <button
            type="button"
            onClick={signOut}
            data-testid="admin-logout"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-white/20 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-white/10"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            Sign Out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:pl-60">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <p className="text-sm font-extrabold text-navy-900">Amden Admin</p>
          <button type="button" onClick={signOut} className="text-xs font-bold text-navy-900">Sign Out</button>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-8 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function AdminOutlet() {
  return <Outlet />;
}
